import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Link2, KanbanSquare, ChevronRight } from 'lucide-react';
import axiosInstance from '../../utils/axiosConfig';
import { PriorityIcon, FechaText } from './utils/displays';

/**
 * Panel compacto "Tareas relacionadas" para fichas de cualquier entidad.
 * Uso:
 *   <TareasRelacionadasPanel entidadTipo="empleado" entidadId={42} />
 *
 * Se monta como una pequeña sección colapsable. Si no hay tareas, no aparece
 * (a menos que pases alwaysShow). Click en una tarea abre su slide-over via
 * navegación a /proyectos/<slug>/tareas/<numero>.
 */

const TareasRelacionadasPanel = ({
  entidadTipo,
  entidadId,
  alwaysShow = false,
  className = '',
}) => {
  const navigate = useNavigate();
  const [tareas, setTareas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  const fetchTareas = useCallback(async () => {
    if (!entidadTipo || !entidadId) return;
    setLoading(true);
    try {
      const { data } = await axiosInstance.get(`/api/entidades/${entidadTipo}/${entidadId}/tareas`);
      setTareas(data || []);
    } catch (err) {
      console.error('Error cargando tareas relacionadas:', err);
      setTareas([]);
    } finally {
      setLoading(false);
    }
  }, [entidadTipo, entidadId]);

  useEffect(() => { fetchTareas(); }, [fetchTareas]);

  if (loading && !alwaysShow) return null;
  if (!loading && tareas.length === 0 && !alwaysShow) return null;

  return (
    <section className={`rounded-xl bg-white border border-slate-200/70 overflow-hidden ${className}`}>
      <button
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-slate-50 transition-colors group"
      >
        <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-cyan-50 text-cyan-600">
          <Link2 size={12} strokeWidth={2} />
        </span>
        <h3 className="text-sm font-semibold text-slate-900 tracking-tight">
          Tareas relacionadas
        </h3>
        <span className="text-[11px] text-slate-400 tabular-nums">
          {tareas.length}
        </span>
        <div className="flex-1" />
        <motion.span
          animate={{ rotate: collapsed ? 0 : 90 }}
          transition={{ duration: 0.15 }}
          className="text-slate-300 group-hover:text-slate-500"
        >
          <ChevronRight size={14} strokeWidth={2.5} />
        </motion.span>
      </button>

      {!collapsed && (
        <div className="border-t border-slate-100">
          {loading ? (
            <div className="px-4 py-6 text-center skeleton">
              <div className="h-3 w-32 bg-slate-100 rounded mx-auto mb-1.5" />
              <div className="h-3 w-20 bg-slate-100 rounded mx-auto" />
            </div>
          ) : tareas.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <KanbanSquare size={20} className="text-slate-300 mx-auto mb-1.5" strokeWidth={1.5} />
              <p className="text-xs text-slate-400">
                Aún no hay tareas vinculadas.
              </p>
            </div>
          ) : (
            <ul>
              {tareas.map((t) => (
                <li key={t.id}>
                  <button
                    onClick={() => navigate(`/proyectos/${t.proyecto_slug}/tareas/${t.numero}`)}
                    className="w-full flex items-center gap-2.5 px-4 py-2 hover:bg-slate-50 transition-colors text-left border-b border-slate-50 last:border-b-0"
                  >
                    <PriorityIcon prioridad={t.prioridad} compact />
                    <span className="font-mono text-[9px] tracking-widest text-slate-400 uppercase">
                      {t.codigo || `${t.proyecto_slug}-${t.numero}`}
                    </span>
                    <span className="text-xs text-slate-800 truncate flex-1">{t.titulo}</span>
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap"
                      style={{
                        backgroundColor: `${t.estado?.color || '#94a3b8'}1f`,
                        color: t.estado?.color || '#475569',
                      }}
                    >
                      <span className="w-1 h-1 rounded-full" style={{ backgroundColor: t.estado?.color || '#94a3b8' }} />
                      {t.estado?.nombre || '—'}
                    </span>
                    <FechaText fecha={t.fecha_vencimiento} compact />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

export default TareasRelacionadasPanel;
